import React from 'react';
import { useTranslation } from 'react-i18next';

import { store, useAppSelector } from '@/app/store';

import { checkNoError } from '@/features/channels/channelsSlice';
import { MasterInfo, copyMasterInfo } from '@/features/master/masterSlice';

import Button from '@/components/Common/Button';
import CheckBox from '@/components/Common/CheckBox';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';

import { CatHeader } from '@/pages/Projects/Project';
import { cs } from '@/pages/Account/accountStyle';

const Mailer = () => {
  const { t } = useTranslation();
  const masterMain = useAppSelector((state) => state.master);

  const [triedSave, setTriedSave] = React.useState(false);
  const [changes, setChanges] = React.useState(false);

  const [master, setMaster] = React.useState(copyMasterInfo(masterMain));

  React.useEffect(() => {
    store.getMaster();
  }, []);

  // Drop local edits whenever the Redux master refreshes (post-save).
  React.useEffect(() => {
    setTriedSave(false);
    setChanges(false);

    setMaster(copyMasterInfo(masterMain));
  }, [masterMain]);

  const smtpHostInvalid = React.useMemo(
    () => !master.smtp_host || master.smtp_host.trim() === '',
    [master.smtp_host]
  );

  const smtpPortInvalid = React.useMemo(() => {
    const p = master.smtp_port ?? 0;
    return p < 0 || p > 65535;
  }, [master.smtp_port]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const smtpFromInvalid = React.useMemo(() => {
    const f = (master.smtp_from ?? '').trim();
    return f === '' || !emailRegex.test(f);
  }, [master.smtp_from]);

  const publicBaseUrlInvalid = React.useMemo(() => {
    const u = (master.public_base_url ?? '').trim();
    if (u === '') return false;
    return !/^https:\/\/[a-zA-Z0-9.\-:]+(\/.*)?$/.test(u);
  }, [master.public_base_url]);

  const anyInvalid =
    smtpHostInvalid || smtpPortInvalid || smtpFromInvalid || publicBaseUrlInvalid;

  const update = React.useCallback(
    (patch: Partial<MasterInfo>) => {
      setMaster({ ...copyMasterInfo(master), ...patch });
      setChanges(true);
    },
    [master]
  );

  const handleSave = React.useCallback(async () => {
    if (anyInvalid) {
      setTriedSave(true);
      return;
    }

    const payload = copyMasterInfo(master);
    delete payload.smtp_password_set;

    const mess = await store.setMaster(payload);
    if (!checkNoError(mess)) return;

    setTriedSave(false);
    setChanges(false);
    store.getMaster();
  }, [master, anyInvalid]);

  const loading = masterMain.loading ? true : false;

  // ---------------------------------------------------------------------------
  // Render. "Configured" status pill mirrors the server's rule
  // (host != "" AND from != "") so admins see the same truth the
  // backend evaluates.
  // ---------------------------------------------------------------------------

  const isConfigured =
    !!(master.smtp_host || '').trim() && !!(master.smtp_from || '').trim();
  const accountInputClassNames = {
    inputWrapper: cs.inputWrapper + ' !bg-white',
    label: cs.inputLabel,
    input: cs.inputInput + ' !text-[#1F1F1F]',
  };

  return (
    <React.Fragment>
      <FlexRow
        direction="column"
        align="start"
        className={[
          'bg-white',
          'px-[32px] pt-[32px] sticky top-[64px] z-10',
        ]}>
        <FlexRow direction="column" align="start" className="pt-0 pb-[120px] gap-[24px] max-w-[560px]">
          <CatHeader caption="mailer:caption" />

          {/* Status pill — green when ready, warm when missing fields. */}
          <FlexRow flex={0} className="gap-[8px] items-center">
            <span
              className="material-icons text-[18px]"
              style={{ color: isConfigured ? '#5B7C5A' : '#B14545' }}>
              {isConfigured ? 'check_circle' : 'error_outline'}
            </span>
            <span
              className="text-[13px]"
              style={{ color: isConfigured ? '#5B7C5A' : '#B14545' }}>
              {isConfigured
                ? t('mailer:status:configured')
                : t('mailer:status:not_configured')}
            </span>
          </FlexRow>

          {/* --- SMTP server section --- */}
          <FlexRow direction="column" align="start" className="gap-[16px] w-full">
            <div className="text-[15px] font-semibold text-[#1F1F1F]">
              {t('mailer:section:smtp_server')}
            </div>

            <FlexRow className="gap-[12px] w-full">
              <Input
                label={t('mailer:smtp_host:caption') + '*'}
                placeholder="smtp.example.com"
                labelPlacement="outside"
                value={master.smtp_host ?? ''}
                isInvalid={triedSave && smtpHostInvalid}
                isLoaded={!loading}
                errorMessage={t('mailer:smtp_host:invalid')}
                classNames={accountInputClassNames}
                onValueChange={(v) => update({ smtp_host: v })}
              />
              <Input
                label={t('mailer:smtp_port:caption')}
                placeholder="587"
                labelPlacement="outside"
                value={String(master.smtp_port ?? 0)}
                isInvalid={triedSave && smtpPortInvalid}
                isLoaded={!loading}
                errorMessage={t('mailer:smtp_port:invalid')}
                classNames={{
                  ...accountInputClassNames,
                  inputWrapper: accountInputClassNames.inputWrapper + ' w-[120px]',
                }}
                onValueChange={(v) => {
                  // Allow blank → 0 (use server default). Reject non-digits.
                  const trimmed = v.trim();
                  if (trimmed === '') {
                    update({ smtp_port: 0 });
                    return;
                  }
                  const n = parseInt(trimmed, 10);
                  if (!isNaN(n)) update({ smtp_port: n });
                }}
              />
            </FlexRow>

            <Input
              label={t('mailer:smtp_username:caption')}
              placeholder={t('mailer:smtp_username:placeholder')}
              labelPlacement="outside"
              value={master.smtp_username ?? ''}
              isLoaded={!loading}
              classNames={accountInputClassNames}
              onValueChange={(v) => update({ smtp_username: v })}
            />

            <Input
              label={t('mailer:smtp_password:caption')}
              labelPlacement="outside"
              type="password"
              placeholder="Enter password"
              value={master.smtp_password ?? ''}
              isLoaded={!loading}
              description={
                master.smtp_password_set
                  ? t('mailer:smtp_password:hint_set')
                  : t('mailer:smtp_password:hint_unset')
              }
              classNames={{
                ...accountInputClassNames,
                description: 'text-[12px] text-[#6B6258] pt-[4px]',
              }}
              onValueChange={(v) => update({ smtp_password: v })}
            />

            <FlexRow flex={0}
                     className="gap-[8px] items-center cursor-pointer select-none"
                     onClick={() => update({ smtp_starttls: !master.smtp_starttls })}>
              <CheckBox
                toggled={!!master.smtp_starttls}
                setToggled={(v: boolean) => update({ smtp_starttls: v })}
                size="text-[20px]"
              />
              <span className="text-[14px] text-[#1F1F1F]">
                {t('mailer:smtp_starttls:caption')}
              </span>
            </FlexRow>
            <div className="text-[12px] text-[#6B6258] -mt-[8px]">
              {t('mailer:smtp_starttls:hint')}
            </div>
          </FlexRow>

          {/* --- From identity --- */}
          <FlexRow direction="column" align="start" className="gap-[16px] w-full">
            <div className="text-[15px] font-semibold text-[#1F1F1F]">
              {t('mailer:section:from')}
            </div>

            <Input
              label={t('mailer:smtp_from:caption') + '*'}
              placeholder="noreply@example.com"
              labelPlacement="outside"
              value={master.smtp_from ?? ''}
              isInvalid={triedSave && smtpFromInvalid}
              isLoaded={!loading}
              errorMessage={t('mailer:smtp_from:invalid')}
              classNames={accountInputClassNames}
              onValueChange={(v) => update({ smtp_from: v })}
            />

            <Input
              label={t('mailer:smtp_from_name:caption')}
              placeholder="Prime Time Middleware"
              labelPlacement="outside"
              value={master.smtp_from_name ?? ''}
              isLoaded={!loading}
              classNames={accountInputClassNames}
              onValueChange={(v) => update({ smtp_from_name: v })}
            />
          </FlexRow>

          {/* --- Public base URL (for reset links) --- */}
          <FlexRow direction="column" align="start" className="gap-[16px] w-full">
            <div className="text-[15px] font-semibold text-[#1F1F1F]">
              {t('mailer:section:public_url')}
            </div>

            <Input
              label={t('mailer:public_base_url:caption')}
              placeholder="https://hotel.example.com"
              labelPlacement="outside"
              value={master.public_base_url ?? ''}
              isInvalid={triedSave && publicBaseUrlInvalid}
              isLoaded={!loading}
              errorMessage={t('mailer:public_base_url:invalid')}
              classNames={accountInputClassNames}
              onValueChange={(v) => update({ public_base_url: v })}
            />
          </FlexRow>
        </FlexRow>
      </FlexRow>
      <FlexRow className="sticky bottom-0 p-[32px] bg-white z-10">
        <Button
          caption={t('mailer:save:caption')}
          icon="save"
          isDisabled={!changes || (triedSave && anyInvalid)}
          onClick={() => handleSave()}
        />
      </FlexRow>
    </React.Fragment>
  );
};

export default Mailer;
